/**
 * @nanokart/tryon-studio-sdk
 * Zustand Store Factory
 */

import { create, StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import {
  TryOnStudioConfig,
  CatalogItem,
  CatalogFilters,
  TryOnResult,
  CustomerContext,
  DEFAULT_API_BASE_URL,
} from '../../types/config';
import { TryOnStore, TryOnStudioState, TryOnStudioActions, SizeRecommendResult } from './types';
import { createApiServices, ApiServices, TryOnApiService, MomentsApiService } from '../api';
import { SDKApiService } from '../api/sdkApi';
import { SizeApiService } from '../api/sizeApi';
import type { CatalogProvider } from '../providers/types';

// ============================================================================
// Initial State
// ============================================================================

const createInitialState = (config: TryOnStudioConfig): TryOnStudioState => ({
  authSession: {
    strategy: 'public-key',
    partnerId: config.partnerId,
    apiKey: config.apiKey,
    headers: { 'X-API-Key': config.apiKey },
  },
  studioSession: {
    id: null,
    status: 'idle',
    startedAt: null,
    stats: { tryonsGenerated: 0, momentsGenerated: 0 },
  },
  photo: {
    file: null,
    preview: null,
    isUploading: false,
    error: null,
  },
  inventory: {
    items: config.items ?? [],
    selectedItem: config.items?.[0] ?? null,
    filters: {},
    pagination: { page: 1, limit: 20, hasMore: false },
    isLoading: false,
    error: null,
  },
  tryon: {
    results: {},
    currentResult: null,
    isGenerating: false,
    error: null,
  },
  moments: {
    results: {},
    isGenerating: null,
    error: null,
  },
  cart: {
    softCart: [],
    hardCart: [],
    isOpen: false,
  },
  partnerConfig: {
    hasAddToCartWebhook: false,
    isLoading: true,
  },
  sizeRecommend: {
    heightInput: '',
    heightUnit: 'cm',
    results: {},
    isLoading: false,
    error: null,
    isAvailable: false,
  },
  ui: {
    activePanel: 'stage',
    modals: {},
    toast: null,
    isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
  },
});

// ============================================================================
// Store Creator
// ============================================================================

type StoreCreator = StateCreator<TryOnStore, [['zustand/devtools', never], ['zustand/immer', never]]>;

const createStoreSlice = (config: TryOnStudioConfig, apiServices?: ApiServices): StoreCreator => {
  // FIX: Per-instance catalog provider — NOT a module-level global.
  // Each createTryOnStore() call gets its own closure variable, preventing
  // multiple SDK instances from stepping on each other.
  let instanceCatalogProvider: CatalogProvider | null = null;

  // Live camera dress captures: productId → File. Kept in the closure so the
  // File is sent directly to the try-on API (never persisted to inventory).
  const cameraGarmentFiles = new Map<string, File>();
  
  // Resolve the API base URL ONCE for all services.
  // NOTE: `??` (not `||`) — an explicit empty string means "same-origin
  // relative URLs". Mixing operators previously sent try-on to the host
  // origin but size/config calls to production, causing 401s on staging.
  const resolvedApiBaseUrl = config.apiBaseUrl ?? DEFAULT_API_BASE_URL;

  // SDK API service for API-key-authenticated endpoints
  const sdkApiService = new SDKApiService(
    resolvedApiBaseUrl,
    config.apiKey
  );

  // Size Recommendation API service
  const sizeApiService = new SizeApiService(
    resolvedApiBaseUrl,
    config.apiKey
  );
  
  // Store customer context from config
  const customerContext: CustomerContext | undefined = config.customerContext;

  return (set, get) => ({
    ...createInitialState(config),

    // ======================== Initialization ========================

    initialize: async () => {
      set((state) => {
        state.studioSession.status = 'active';
        state.studioSession.startedAt = new Date().toISOString();
      });
      
      // Check partner's webhook configuration using SDK API (API key auth)
      try {
        const sdkConfig = await sdkApiService.getConfig();
        set((state) => {
          state.partnerConfig.hasAddToCartWebhook = sdkConfig.features.add_to_cart_webhook;
          state.partnerConfig.isLoading = false;
        });
      } catch {
        // If check fails, assume no webhook configured
        set((state) => {
          state.partnerConfig.hasAddToCartWebhook = false;
          state.partnerConfig.isLoading = false;
        });
      }
    },

    reset: () => {
      set(createInitialState(config));
    },

    // ======================== Photo ========================

    setPhotoFile: (file: File) => {
      set((state) => {
        state.photo.file = file;
        state.photo.error = null;
        // FIX: Clear ALL cached try-on and moment results — they used the old photo.
        // Showing old-photo results after a new photo is uploaded is a UX/data bug.
        state.tryon.currentResult = null;
        state.tryon.results = {};
        state.tryon.error = null;
        state.moments.results = {};
        state.moments.error = null;
        // Clear size recommendation results — they depend on the photo
        state.sizeRecommend.results = {};
        state.sizeRecommend.error = null;
      });
    },

    setPhotoPreview: (preview: string) => {
      set((state) => { state.photo.preview = preview; });
    },

    clearPhoto: () => {
      set((state) => {
        state.photo.file = null;
        state.photo.preview = null;
        state.photo.isUploading = false;
        state.photo.error = null;
        // FIX: Clear cached results — they're meaningless without a photo.
        state.tryon.currentResult = null;
        state.tryon.results = {};
        state.moments.results = {};
        // Clear size recommendation results — they depend on the photo
        state.sizeRecommend.results = {};
        state.sizeRecommend.error = null;
      });
    },

    // ======================== Inventory ========================

    setCatalogProvider: (provider: CatalogProvider) => {
      instanceCatalogProvider = provider;
      get().loadInventory();
    },

    loadInventory: async (filters?: CatalogFilters, append = false) => {
      const currentPage = get().inventory.pagination.page;

      set((state) => {
        state.inventory.isLoading = true;
        state.inventory.error = null;
        if (filters) {
          state.inventory.filters = { ...state.inventory.filters, ...filters };
        }
        if (!append) {
          state.inventory.pagination.page = 1;
        }
        // FIX: Do NOT increment page here. Increment only on success to prevent
        // the page counter from drifting if the API call fails.
      });

      if (!instanceCatalogProvider) {
        set((state) => { state.inventory.isLoading = false; });
        return;
      }

      // Determine the page to request
      const nextPage = append ? currentPage + 1 : 1;

      try {
        const currentState = get();
        const mergedFilters = { ...currentState.inventory.filters, ...filters };
        const pagination = {
          page: nextPage,
          limit: currentState.inventory.pagination.limit,
        };

        const result = await instanceCatalogProvider.list(mergedFilters, pagination);

        set((state) => {
          if (append) {
            // Deduplicate by productId before appending
            const existingIds = new Set(state.inventory.items.map((i) => i.productId));
            const newItems = result.items.filter((i) => !existingIds.has(i.productId));
            state.inventory.items = [...state.inventory.items, ...newItems];
            // FIX: Only update page counter on success
            state.inventory.pagination.page = nextPage;
          } else {
            // Preserve any live camera captures across reloads
            const cameraItems = state.inventory.items.filter(
              (i) => i.metadata?.isCameraCapture
            );
            state.inventory.items = [...cameraItems, ...result.items];
            state.inventory.pagination.page = 1;
          }
          state.inventory.pagination.hasMore = result.hasMore;
          state.inventory.isLoading = false;
          state.inventory.error = null;
          // Auto-select first item if none selected
          if (!state.inventory.selectedItem && result.items.length > 0) {
            state.inventory.selectedItem = result.items[0];
          }
        });
      } catch (error) {
        set((state) => {
          state.inventory.isLoading = false;
          state.inventory.error =
            error instanceof Error ? error.message : 'Failed to load catalog';
          // Page stays at currentPage on failure (not nextPage)
        });
      }
    },

    selectItem: (item: CatalogItem | null) => {
      set((state) => { state.inventory.selectedItem = item; });
    },

    addCameraGarment: async (file: File) => {
      const preview: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read captured image'));
        reader.readAsDataURL(file);
      });

      const captureCount = Array.from(cameraGarmentFiles.keys()).length + 1;
      const item: CatalogItem = {
        productId: `camera-garment-${Date.now()}`,
        name: `Camera Dress ${captureCount}`,
        imageUrl: preview,
        thumbnailUrl: preview,
        price: 0,
        currency: config.currency ?? 'INR',
        metadata: { isCameraCapture: true },
      };
      cameraGarmentFiles.set(item.productId, file);

      set((state) => {
        state.inventory.items = [item, ...state.inventory.items];
        state.inventory.selectedItem = item;
      });

      return item;
    },

    updateFilters: (filters: Partial<CatalogFilters>) => {
      set((state) => {
        state.inventory.filters = { ...state.inventory.filters, ...filters };
      });
    },

    loadMore: async () => {
      const { pagination, isLoading } = get().inventory;
      if (isLoading || !pagination.hasMore) return;
      // FIX: Don't increment page here — loadInventory handles it on success
      await get().loadInventory(undefined, true);
    },

    // ======================== Try-On ========================

    executeTryOn: async (productId: string) => {
      // Return cached result without mutating currentResult state for cache-hits
      // that come from history navigation (handled in MainStage via getCachedResult)
      const cached = get().tryon.results[productId];
      if (cached) {
        set((state) => { state.tryon.currentResult = cached; });
        return cached;
      }

      set((state) => {
        state.tryon.isGenerating = true;
        state.tryon.error = null;
      });

      try {
        const photoFile = get().photo.file;
        const photoPreview = get().photo.preview;
        const selectedItem = get().inventory.selectedItem;

        if (!photoFile) throw new Error('Please upload your photo first');
        if (!selectedItem) throw new Error('Please select a garment first');
        if (!apiServices) throw new Error('SDK not initialized. Provide a valid apiKey.');

        const garmentImageUrl = selectedItem.imageUrl;
        // Camera-captured dress → send the File itself (data URLs can't be fetched server-side)
        const garmentFile = cameraGarmentFiles.get(productId);

        const response = await apiServices.tryon.generateTryOn(
          photoFile,
          garmentFile ? '' : garmentImageUrl,
          // Pass SKU ID from metadata if available (for backend analytics)
          (selectedItem.metadata?.skuId as string | undefined) ?? selectedItem.productId,
          undefined,
          garmentFile
        );

        const result = TryOnApiService.mapToTryOnResult(
          response,
          productId,
          photoPreview ?? '',
          garmentImageUrl
        );

        set((state) => {
          state.tryon.results[productId] = result;
          state.tryon.currentResult = result;
          state.tryon.isGenerating = false;
          state.studioSession.stats.tryonsGenerated += 1;
        });

        return result;
      } catch (error) {
        const errorMessage =
          (error as { message?: string })?.message || 'Try-on failed. Please try again.';
        set((state) => {
          state.tryon.isGenerating = false;
          state.tryon.error = errorMessage;
        });
        throw error;
      }
    },

    getCachedResult: (productId: string) => {
      return get().tryon.results[productId] ?? null;
    },

    clearTryOnError: () => {
      set((state) => { state.tryon.error = null; });
    },

    // ======================== Moments ========================

    generateMoment: async (tryonCacheKey: string, momentType: string) => {
      // Return cached moment result if already generated
      const existing = get().moments.results[tryonCacheKey]?.find(
        (r) => r.momentType === momentType
      );
      if (existing) return existing;

      set((state) => {
        state.moments.isGenerating = momentType;
        state.moments.error = null;
      });

      try {
        if (!apiServices) throw new Error('SDK not initialized. Provide a valid apiKey.');

        const response = await apiServices.moments.generateMoment(tryonCacheKey, momentType);
        const result = MomentsApiService.mapToMomentResult(response, tryonCacheKey);

        set((state) => {
          if (!state.moments.results[tryonCacheKey]) {
            state.moments.results[tryonCacheKey] = [];
          }
          // Prevent duplicates
          const exists = state.moments.results[tryonCacheKey].some(
            (r) => r.momentType === result.momentType
          );
          if (!exists) {
            state.moments.results[tryonCacheKey].push(result);
          }
          state.moments.isGenerating = null;
          state.studioSession.stats.momentsGenerated += 1;
        });

        return result;
      } catch (error) {
        const errorMessage =
          (error as { message?: string })?.message || 'Moment generation failed';
        set((state) => {
          state.moments.isGenerating = null;
          state.moments.error = errorMessage;
        });
        throw error;
      }
    },

    getMomentResults: (tryonCacheKey: string) => {
      return get().moments.results[tryonCacheKey] ?? [];
    },

    // ======================== Cart ========================

    addToSoftCart: (item: CatalogItem, tryonResult?: TryOnResult) => {
      set((state) => {
        const exists = state.cart.softCart.some((c) => c.productId === item.productId);
        if (!exists) {
          state.cart.softCart.push({
            productId: item.productId,
            product: item,
            quantity: 1,
            tryonResult,
            addedAt: new Date().toISOString(),
          });
        }
      });
    },

    addToHardCart: (item: CatalogItem, size?: string) => {
      set((state) => {
        const existingIndex = state.cart.hardCart.findIndex(
          (c) => c.productId === item.productId && c.size === size
        );
        if (existingIndex >= 0) {
          state.cart.hardCart[existingIndex].quantity += 1;
        } else {
          const softCartItem = state.cart.softCart.find((c) => c.productId === item.productId);
          state.cart.hardCart.push({
            productId: item.productId,
            product: item,
            size,
            quantity: 1,
            tryonResult: softCartItem?.tryonResult,
            addedAt: new Date().toISOString(),
          });
        }
      });
    },

    removeFromCart: (productId: string, cartType: 'soft' | 'hard') => {
      set((state) => {
        if (cartType === 'soft') {
          state.cart.softCart = state.cart.softCart.filter((c) => c.productId !== productId);
        } else {
          state.cart.hardCart = state.cart.hardCart.filter((c) => c.productId !== productId);
        }
      });
    },

    toggleCartDrawer: () => {
      set((state) => { state.cart.isOpen = !state.cart.isOpen; });
    },

    clearCart: () => {
      set((state) => {
        state.cart.softCart = [];
        state.cart.hardCart = [];
      });
    },

    // ======================== Size Recommendation ========================

    setSizeHeightInput: (value: string, unit: 'cm' | 'ft') => {
      set((state) => {
        state.sizeRecommend.heightInput = value;
        state.sizeRecommend.heightUnit = unit;
      });
    },

    clearSizeRecommendation: (productId?: string) => {
      set((state) => {
        if (productId) {
          delete state.sizeRecommend.results[productId];
        } else {
          state.sizeRecommend.results = {};
        }
        state.sizeRecommend.error = null;
      });
    },

    checkSizeAvailability: async (productId: string) => {
      const item = get().inventory.items.find((i) => i.productId === productId)
        ?? get().inventory.selectedItem;
      if (!item) {
        set((state) => { state.sizeRecommend.isAvailable = false; });
        return;
      }
      // Camera-captured dresses are not inventory products — no size chart exists,
      // so skip the backend lookup entirely (avoids pointless 401/404 calls).
      if (item.metadata?.isCameraCapture) {
        set((state) => { state.sizeRecommend.isAvailable = false; });
        return;
      }
      // If size chart is already in the catalog item, mark available
      if (item.sizeChartCm) {
        set((state) => { state.sizeRecommend.isAvailable = true; });
        return;
      }
      // Otherwise try to fetch from backend
      try {
        const chart = await sizeApiService.getSizeChart(productId);
        set((state) => {
          state.sizeRecommend.isAvailable = chart.has_size_chart;
        });
        // Patch the catalog item in state so we don't re-fetch
        if (chart.has_size_chart && chart.size_chart_cm) {
          set((state) => {
            const idx = state.inventory.items.findIndex((i) => i.productId === productId);
            if (idx >= 0) {
              state.inventory.items[idx].sizeChartCm = chart.size_chart_cm as Record<string, Record<string, number>>;
              state.inventory.items[idx].gender = state.inventory.items[idx].gender ?? chart.gender;
            }
            if (state.inventory.selectedItem?.productId === productId) {
              state.inventory.selectedItem.sizeChartCm = chart.size_chart_cm as Record<string, Record<string, number>>;
              state.inventory.selectedItem.gender = state.inventory.selectedItem.gender ?? chart.gender;
            }
          });
        }
      } catch {
        set((state) => { state.sizeRecommend.isAvailable = false; });
      }
    },

    fetchSizeRecommendation: async (productId: string, heightCm: number): Promise<SizeRecommendResult | null> => {
      // Return cached result if available
      const cached = get().sizeRecommend.results[productId];
      if (cached) return cached;

      // Use the customer's photo file directly (decoupled from try-on result)
      const photoFile = get().photo.file;
      if (!photoFile) {
        set((state) => {
          state.sizeRecommend.error = 'Please select your photo first to get a size recommendation.';
        });
        return null;
      }

      const selectedItem = get().inventory.selectedItem;
      if (!selectedItem) {
        set((state) => { state.sizeRecommend.error = 'No product selected.'; });
        return null;
      }

      let sizeChart = selectedItem.sizeChartCm;
      if (!sizeChart) {
        // Last-chance fetch
        try {
          const chart = await sizeApiService.getSizeChart(productId);
          if (chart.has_size_chart && chart.size_chart_cm) {
            sizeChart = chart.size_chart_cm as Record<string, Record<string, number>>;
          }
        } catch {
          // ignore
        }
      }

      if (!sizeChart) {
        set((state) => {
          state.sizeRecommend.error = 'This product does not have a size chart.';
        });
        return null;
      }

      set((state) => {
        state.sizeRecommend.isLoading = true;
        state.sizeRecommend.error = null;
      });

      try {
        const gender = selectedItem.gender
          ?? (selectedItem.metadata?.gender as string | undefined)
          ?? 'unisex';

        // Calls POST /api/v1/size/recommend-from-photo on https://api.nanokart.ai
        const response = await sizeApiService.getSizeRecommendFromPhotoFile(photoFile, {
          height_cm: heightCm,
          size_chart_cm: sizeChart as Record<string, Record<string, number>>,
          product_id: productId,
          sku_id: selectedItem.variantId,
          gender,
        });

        const result: SizeRecommendResult = {
          recommendedSize: response.recommended_size,
          confidencePercent: response.confidence_percent,
          fitNotes: response.fit_notes,
          photoSufficient: response.photo_sufficient,
          insufficiencyMessage: response.insufficiency_message,
          engine: response.engine,
          cached: response.cached,
          creditsConsumed: response.credits_consumed,
        };

        set((state) => {
          state.sizeRecommend.results[productId] = result;
          state.sizeRecommend.isLoading = false;
        });

        return result;
      } catch (error) {
        const msg = (error as { message?: string })?.message ?? 'Size recommendation failed.';
        set((state) => {
          state.sizeRecommend.isLoading = false;
          state.sizeRecommend.error = msg;
        });
        return null;
      }
    },

    // ======================== SDK Webhook ========================
    
    invokeAddToCartWebhook: async (item: CatalogItem, tryonResult?: TryOnResult, size?: string): Promise<boolean> => {      try {
        const result = await sdkApiService.addToCart(
          {
            product_id: item.productId,
            product_name: item.name,
            sku_id: item.variantId,
            size: size || item.sizes?.[0],
            color: item.color,
            quantity: 1,
            price: item.price,
            currency: item.currency,
            tryon_image_url: tryonResult?.resultImageUrl,
            garment_image_url: item.imageUrl,
            metadata: item.metadata,
          },
          customerContext
        );
        return result.success;
      } catch (error) {
        console.error('[Store] Failed to invoke add to cart webhook:', error);
        return false;
      }
    },

    // ======================== Purchase Intent ========================

    recordPurchaseIntent: async (item: CatalogItem, _tryonResult?: TryOnResult, size?: string): Promise<void> => {
      try {
        await sdkApiService.recordPurchaseIntent({
          product_id: item.productId,
          product_name: item.name,
          sku_id: item.variantId,
          price: item.price,
          quantity: 1,
          currency: item.currency,
          source: 'sdk',
          // Customer context injected by partner at SDK init
          customer_phone: customerContext?.phone,
          customer_email: customerContext?.email,
          customer_name: customerContext?.name,
          customer_id: customerContext?.customerId,
          metadata: {
            size: size || item.sizes?.[0],
            color: item.color,
            ...(item.metadata ?? {}),
          },
        });
      } catch (error) {
        // Silent fail — never block the cart UX
        console.error('[Store] recordPurchaseIntent failed:', error);
      }
    },

    // ======================== UI ========================

    setActivePanel: (panel) => {
      set((state) => { state.ui.activePanel = panel; });
    },

    openModal: (modalId: string) => {
      set((state) => { state.ui.modals[modalId] = true; });
    },

    closeModal: (modalId: string) => {
      set((state) => { state.ui.modals[modalId] = false; });
    },

    showToast: (message: string, type: 'success' | 'error' | 'info') => {
      set((state) => { state.ui.toast = { message, type }; });
      setTimeout(() => { get().clearToast(); }, 3500);
    },

    clearToast: () => {
      set((state) => { state.ui.toast = null; });
    },

    endSession: (_reason) => {
      set((state) => { state.studioSession.status = 'ended'; });
    },
  });
};

// ============================================================================
// Store Factory
// ============================================================================

export function createTryOnStore(config: TryOnStudioConfig) {
  const baseUrl = config.apiBaseUrl ?? DEFAULT_API_BASE_URL;
  const apiServices = createApiServices(config.apiKey, baseUrl);

  return create<TryOnStore>()(
    devtools(
      immer(createStoreSlice(config, apiServices)),
      { name: 'NanokartTryOnStudio' }
    )
  );
}

// ============================================================================
// Export Types
// ============================================================================

export type { TryOnStore, TryOnStudioState, TryOnStudioActions } from './types';
